import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {useHasFullSchedulerAccess} from '../../scheduler/create-modals/multi-host/use-has-full-scheduler-access';
import {useGetCalendarSync} from '../../setup/calendar/graphql/hooks';

import {CouponOffersSection} from './coupon-offers-section';
import {EmailCalendarSyncModal as EmailCalendarSyncModalOld} from './email-calendar-sync-modal/email-calendar-sync-modal';
import type {EmailCalendarSyncSettings} from './email-calendar-sync-modal/email-calendar-sync-settings-page';
import {EmailCalendarSyncModal} from './email-calendar-sync-modal-new/email-calendar-sync-modal';
import {ExplorePageBase} from './explore-page-base';
import {useGetCustomerOnboarding} from './graphql/hooks/use-get-customer-onboarding';
import {useGetEmailCalendarStatuses} from './graphql/hooks/use-get-email-calendar-statuses';
import {useGetUserVideoIntegrations} from './graphql/hooks/use-get-user-video-integrations';
import {useGoogleSetInitialSyncSettings} from './graphql/hooks/use-google-set-initial-sync-settings';
import {useOffice365SetInitialSyncSettings} from './graphql/hooks/use-office365-set-initial-sync-settings';
import {useOnboardingRefetchListeners} from './hooks/use-onboarding-refetch-listeners';
import {IntroWebcastBanner} from './intro-webcast-banner';
import {MakeNutshellBetterSection} from './make-nutshell-better-section';
import {NutshellAiSection} from './nutshell-ai-section';
import {HiddenModulesSection} from './onboarding-module/hidden-modules-section';
import {OnboardingModulesList} from './onboarding-module/onboarding-modules-list';
import {ProductUpdatesSection} from './product-updates/product-updates-section';
import {UpcomingOffersSection} from './upcoming-offers/upcoming-offers-section';

export function CustomerExplorePage() {
    const {user, isLoading: isUserLoading} = useGetSessionUser();

    const {customerOnboarding, isLoading, refetch} = useGetCustomerOnboarding();
    const {hasFullSchedulerAccess} = useHasFullSchedulerAccess();
    const {
        userEmailCalendarStatuses,
        isLoading: isEmailCalendarStatusesLoading,
        refetch: refetchEmailCalendarStatuses,
    } = useGetEmailCalendarStatuses(!NutClientConfig.hasImprovedEmailCalendarSyncFF);
    const {userVideoIntegrations} = useGetUserVideoIntegrations();
    const {calendarSync, refetch: refetchCalendarSync} = useGetCalendarSync();
    const [isEmailCalendarSyncModalOpen, setIsEmailCalendarSyncModalOpen] = React.useState(false);
    const [connectedProvider, setConnectedProvider] = React.useState<'google' | 'microsoft' | null>(
        null
    );
    useOnboardingRefetchListeners(refetch, refetchEmailCalendarStatuses);
    const {googleSetInitialSyncSettings} = useGoogleSetInitialSyncSettings();
    const {office365SetInitialSyncSettings} = useOffice365SetInitialSyncSettings();

    const handleEmailCalendarAuthSuccess = React.useCallback(
        (provider: 'google' | 'microsoft') => {
            setConnectedProvider(provider);
            refetchCalendarSync();
            setIsEmailCalendarSyncModalOpen(true);
            window.dispatchEvent(new Event('onboarding-step-completed-email-calendar-sync'));
        },
        [refetchCalendarSync]
    );

    const handleSyncSettingsSave = React.useCallback(
        (settings: EmailCalendarSyncSettings): Promise<any> => {
            if (!connectedProvider) {
                return Promise.resolve();
            }

            const mutation =
                connectedProvider === 'google'
                    ? googleSetInitialSyncSettings
                    : office365SetInitialSyncSettings;

            return mutation({variables: {input: settings}});
        },
        [connectedProvider, googleSetInitialSyncSettings, office365SetInitialSyncSettings]
    );

    const shouldShowLoadingState =
        (isLoading || isEmailCalendarStatusesLoading || isUserLoading) && !customerOnboarding;

    const clickToCallIntegration = user ? user.clickToCallIntegration : null;
    const canUseClickToCall = NutCapabilities.clickToCall.isAvailable;
    const hasEnabledPhoneIntegration = Boolean(
        user && user.phoneIntegrations.some((integration) => integration.isEnabledForUser === true)
    );

    return (
        <ExplorePageBase variant='customer'>
            <ProductUpdatesSection />
            <CouponOffersSection />
            <Stack spacing={16}>
                <OnboardingModulesList
                    title='Get the most out of Nutshell'
                    modules={customerOnboarding && customerOnboarding.shownModules}
                    isLoading={shouldShowLoadingState}
                    hasFullSchedulerAccess={hasFullSchedulerAccess}
                    userEmailCalendarStatuses={userEmailCalendarStatuses}
                    onboardingQuotes={null}
                    userVideoIntegrations={userVideoIntegrations}
                    hasClickToCallConnected={canUseClickToCall && clickToCallIntegration?.isEnabled}
                    hasPhoneIntegrationConnected={hasEnabledPhoneIntegration}
                    variant='customer'
                    additionalOnboarding={<IntroWebcastBanner canDismiss={true} />}
                    onEmailCalendarAuthSuccess={handleEmailCalendarAuthSuccess}
                />
                {customerOnboarding && customerOnboarding.hiddenModules.length > 0 && (
                    <HiddenModulesSection
                        hiddenModules={customerOnboarding.hiddenModules}
                        hasFullSchedulerAccess={hasFullSchedulerAccess}
                        userEmailCalendarStatuses={userEmailCalendarStatuses}
                        userVideoIntegrations={userVideoIntegrations}
                        hasClickToCallConnected={
                            canUseClickToCall && clickToCallIntegration?.isEnabled
                        }
                        hasPhoneIntegrationConnected={hasEnabledPhoneIntegration}
                        onEmailCalendarAuthSuccess={handleEmailCalendarAuthSuccess}
                    />
                )}
            </Stack>
            <NutshellAiSection />
            <UpcomingOffersSection />
            <MakeNutshellBetterSection />
            {NutClientConfig.hasNewEmailSyncContactCreationFF ? (
                <EmailCalendarSyncModal
                    isOpen={isEmailCalendarSyncModalOpen}
                    onClose={() => setIsEmailCalendarSyncModalOpen(false)}
                    onSaveSyncSettings={handleSyncSettingsSave}
                    calendarSync={calendarSync}
                />
            ) : (
                <EmailCalendarSyncModalOld
                    isOpen={isEmailCalendarSyncModalOpen}
                    onClose={() => setIsEmailCalendarSyncModalOpen(false)}
                    onSaveSyncSettings={handleSyncSettingsSave}
                    calendarSync={calendarSync}
                />
            )}
        </ExplorePageBase>
    );
}
